import {
    applyCashPolicyMonth,
    buildCashPolicyResult,
    effectiveFloor,
    interestForMonth,
    policyOwnedLineKeys,
    safeExcessSweeps,
} from './cashPolicy';
import { applyCashFlow } from './cashFlow';
import { loadCashFlowSchema } from './cashFlowSchema';
import type {
    CaseInputs,
    CashPolicyMonthResult,
    EvaluateContext,
    Formula,
    History,
    Model,
    OneTimeItem,
    RecipeDefaults,
    SchemaEntry,
    View,
} from './types';
import { LOCATION_VIEWS } from './types';
import { daysInMonth, monthKeyFromT } from './calendar';
import {
    AVG_TICKET_KEY,
    GPS_KEY,
    consolidateIsFromLocations,
} from './consolidate';
import { applyDriver, applyLinked, applyPlug } from './drivers';
import { evalFormula, formulaDependencies } from './formulas';
import { indexSchema } from './schema';
import {
    ignoreGroupForParent,
    isGroupIgnored,
    isGroupIgnoredAtAnyLocation,
    listEligibleIgnoreGroups,
    parentGroupIfIgnored,
} from './ignoreGroups';

const LABOR_COMPONENT_KEYS = [
    'is.hourly_fte_boh',
    'is.hourly_fte_foh',
    'is.avg_hourly_rate_boh',
    'is.avg_hourly_rate_foh',
    'is.salaried_headcount',
    'is.avg_salary_annual',
];

const OPERATIONAL_KEYS = [
    'is.square_footage',
    'is.store_status',
    'is.days_closed',
    ...LABOR_COMPONENT_KEYS,
    'is.boh',
    'is.foh',
    'is.avg_orders_per_day',
    'is.avg_ticket',
];

const PAYROLL_WAGES_KEY = 'is.payroll_wages';

const TOTAL_ORDERS_KEY = 'is.total_orders';
const AVG_ORDERS_KEY = 'is.avg_orders_per_day';
const DAYS_OPEN_KEY = 'is.days_open_for_business';
const DAYS_CLOSED_KEY = 'is.days_closed';

const CASH_KEY = 'bs.cash_bank_accounts_and_petty_cash';
const MM_KEY = 'bs.cash_equivalents';
const TOTAL_CASH_KEY = 'bs.total_cash_and_equivalents';
const TOTAL_ASSETS_KEY = 'bs.total_assets';
const TOTAL_LE_KEY = 'bs.total_liabilities_and_equity';
const INTEREST_KEY = 'is.plus_interest_income';
const DEPR_KEY = 'is.depreciation_expense';
const AMORT_KEY = 'is.amortization_expense';
const NI_KEY = 'is.net_income_loss';
const LINKED_BS: Record<string, string> = {
    'bs.less_accumulated_depreciation': DEPR_KEY,
    'bs.less_accumulated_amortization': AMORT_KEY,
    'bs.plus_net_income': NI_KEY,
};
const SKIP_FORMULA_IN_PROJECTION = new Set([
    'bs.less_accumulated_depreciation',
    'bs.less_accumulated_amortization',
]);

function getCell(
    model: Model,
    view: View,
    lineKey: string,
    t: number,
): number | string | null {
    return model.values[view]?.[lineKey]?.[t] ?? null;
}

function getNumeric(model: Model, view: View, lineKey: string, t: number): number | null {
    const value = getCell(model, view, lineKey, t);
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function setCell(
    model: Model,
    view: View,
    lineKey: string,
    t: number,
    value: number | string | null,
): void {
    if (!model.values[view][lineKey]) {
        model.values[view][lineKey] = {};
    }
    model.values[view][lineKey]![t] = value;
}

function emptyModel(nHistory: number): Model {
    const views: View[] = [...LOCATION_VIEWS, 'consolidated'];
    const values = Object.fromEntries(
        views.map((view) => [view, {} as Record<string, Record<number, number | string | null>>]),
    ) as Model['values'];
    return {
        nHistory,
        values,
        kpis: {
            net_income: { t3: null, t6: null, t12: null },
            cash: { t3: null, t6: null, t12: null },
            total_cash: { t3: null, t6: null, t12: null },
        },
        cash_policy: null,
        diagnostics: [],
    };
}

function loadHistory(model: Model, history: History, schema: SchemaEntry[]): void {
    const nHistory = history.nHistory;
    const emptyInputsForHistory = emptyInputs();

    for (const view of LOCATION_VIEWS) {
        for (const [lineKey, series] of Object.entries(history.values[view] ?? {})) {
            for (let i = 0; i < nHistory; i += 1) {
                const t = i - (nHistory - 1);
                setCell(model, view, lineKey, t, series[i] ?? null);
            }
        }
        for (let i = 0; i < nHistory; i += 1) {
            const t = i - (nHistory - 1);
            const totalOrders = getCell(model, view, TOTAL_ORDERS_KEY, t);
            const daysOpen = getCell(model, view, DAYS_OPEN_KEY, t);
            let avgOrders: number | null = null;
            if (typeof daysOpen === 'number' && daysOpen !== 0 && typeof totalOrders === 'number') {
                avgOrders = totalOrders / daysOpen;
            }
            setCell(model, view, AVG_ORDERS_KEY, t, avgOrders);
        }
    }

    for (let i = 0; i < nHistory; i += 1) {
        const t = i - (nHistory - 1);
        consolidateIsFromLocations(
            schema,
            emptyInputsForHistory,
            (view, key) => getCell(model, view, key, t),
            (key) => getCell(model, 'consolidated', key, t),
            (key, value) => setCell(model, 'consolidated', key, t, value),
        );
    }

    for (const [lineKey, series] of Object.entries(history.values.consolidated ?? {})) {
        const entry = schema.find((row) => row.key === lineKey);
        if (entry?.tab !== 'BS') {
            continue;
        }
        for (let i = 0; i < nHistory; i += 1) {
            const t = i - (nHistory - 1);
            setCell(model, 'consolidated', lineKey, t, series[i] ?? null);
        }
    }

    for (const entry of loadCashFlowSchema()) {
        const series = history.values.consolidated?.[entry.key];
        if (!series) {
            continue;
        }
        for (let i = 0; i < nHistory; i += 1) {
            const t = i - (nHistory - 1);
            setCell(model, 'consolidated', entry.key, t, series[i] ?? null);
        }
    }
}

function driverInput(
    inputs: CaseInputs,
    view: View,
    lineKey: string,
    t: number,
): number | null {
    const state = inputs.drivers[view]?.[lineKey];
    if (!state?.active) {
        return null;
    }
    return state.by_type[state.active]?.values[t - 1] ?? null;
}

function activeDriver(inputs: CaseInputs, view: View, lineKey: string) {
    return inputs.drivers[view]?.[lineKey]?.active ?? null;
}

function driverParams(inputs: CaseInputs, view: View, lineKey: string) {
    const state = inputs.drivers[view]?.[lineKey];
    if (!state?.active) {
        return undefined;
    }
    return state.by_type[state.active]?.params;
}

function collectFormulaDeps(formula: Formula | number | string | null | undefined): string[] {
    return formulaDependencies(formula);
}

function makeValueOf(
    model: Model,
    view: View,
    t: number,
    schemaByKey: Record<string, SchemaEntry>,
    inputs: CaseInputs,
    schema: SchemaEntry[],
): (lineKey: string) => number | null {
    const memo = new Map<string, number | null>();

    const valueOf = (lineKey: string): number | null => {
        if (memo.has(lineKey)) {
            return memo.get(lineKey)!;
        }

        const entry = schemaByKey[lineKey];
        if (!entry) {
            memo.set(lineKey, null);
            return null;
        }

        const existing = model.values[view]?.[lineKey];
        const skipExistingShortcut =
            view === 'consolidated' &&
            (entry.kind === 'subtotal' || entry.kind === 'metric') &&
            entry.formula &&
            lineKey !== GPS_KEY &&
            lineKey !== AVG_TICKET_KEY;
        if (existing && Object.prototype.hasOwnProperty.call(existing, t) && !skipExistingShortcut) {
            const value = getNumeric(model, view, lineKey, t);
            memo.set(lineKey, value);
            return value;
        }

        if (entry.kind === 'input') {
            const value = getNumeric(model, view, lineKey, t);
            memo.set(lineKey, value);
            return value;
        }

        if ((entry.kind === 'subtotal' || entry.kind === 'metric') && entry.formula) {
            const ignoredParent = parentGroupIfIgnored(schema, inputs, view, lineKey);
            if (ignoredParent) {
                const value = getNumeric(model, view, lineKey, t);
                memo.set(lineKey, value);
                return value;
            }
            const valuesByKey: Record<string, number | null> = {};
            const deps = collectFormulaDeps(entry.formula);
            for (const dep of deps) {
                const depEntry = schemaByKey[dep];
                if (depEntry?.kind === 'input') {
                    // Coerce only inside formula arithmetic so Gross − undriven Cat 2
                    // still yields GPS. Input cells themselves stay null.
                    valuesByKey[dep] = getNumeric(model, view, dep, t) ?? 0;
                } else {
                    valuesByKey[dep] = valueOf(dep);
                }
            }
            const result = evalFormula(entry.formula, valuesByKey);
            memo.set(lineKey, result);
            return result;
        }

        memo.set(lineKey, null);
        return null;
    };

    return valueOf;
}

function applyOneTimeItems(
    model: Model,
    inputs: CaseInputs,
    schemaByKey: Record<string, SchemaEntry>,
    view: View,
    lineKey: string,
    t: number,
    lastClosed: string,
): void {
    const month = monthKeyFromT(lastClosed, t);
    for (const item of inputs.one_time_items) {
        if (item.view !== view || item.line_key !== lineKey || item.month !== month) {
            continue;
        }
        const entry = schemaByKey[lineKey];
        if (!entry || entry.kind !== 'input') {
            continue;
        }
        if (item.mode === 'incremental') {
            const current = getNumeric(model, view, lineKey, t) ?? 0;
            setCell(model, view, lineKey, t, current + item.amount);
        } else {
            model.diagnostics.push({
                code: 'one_time_already_in_driver',
                message: `One-time item ${item.id} already in driver`,
                view,
                lineKey,
                t,
            });
        }
    }
}

function isLaborBuildupActive(inputs: CaseInputs, view: View): boolean {
    if (view === 'consolidated') {
        return false;
    }
    return inputs.drivers[view]?.[PAYROLL_WAGES_KEY]?.active === 'labor_buildup';
}

function makeCtx(
    model: Model,
    inputs: CaseInputs,
    schemaByKey: Record<string, SchemaEntry>,
    lastClosed: string,
    view: View,
    t: number,
    valueOf: (lineKey: string) => number | null,
    recipeDefaults?: RecipeDefaults,
) {
    return {
        getNumeric: (ctxView: View, lineKey: string, monthT: number) => {
            if (ctxView === view && monthT === t) {
                return valueOf(lineKey);
            }
            return getNumeric(model, ctxView, lineKey, monthT);
        },
        daysInMonth: (monthT: number) => daysInMonth(lastClosed, monthT),
        monthKey: (monthT: number) => monthKeyFromT(lastClosed, monthT),
        schemaByKey,
        driverInput: (ctxView: View, lineKey: string, monthT: number) =>
            driverInput(inputs, ctxView, lineKey, monthT),
        driverParams: (ctxView: View, lineKey: string) => driverParams(inputs, ctxView, lineKey),
        addDiagnostic: (code: string, message: string, diagView?: View, lineKey?: string, monthT?: number) => {
            model.diagnostics.push({ code, message, view: diagView, lineKey, t: monthT });
        },
        view,
        recipeDefaults,
        cashPolicyAnchor: inputs.cash_policy?.distribution?.anchor_date ?? null,
    };
}

function driveLine(
    model: Model,
    inputs: CaseInputs,
    schema: SchemaEntry[],
    schemaByKey: Record<string, SchemaEntry>,
    view: View,
    lineKey: string,
    t: number,
    lastClosed: string,
    valueOf: (lineKey: string) => number | null,
    policyOwnedKeys: Set<string>,
    forcedDriver?: string,
): void {
    const entry = schemaByKey[lineKey];
    if (!entry) {
        return;
    }

    if (entry.kind === 'text') {
        if (entry.drivers.includes('carry_forward')) {
            const prev = getCell(model, view, lineKey, t - 1);
            setCell(model, view, lineKey, t, prev);
        }
        return;
    }

    if (forcedDriver === 'orders_x_ticket') {
        const ctx = makeCtx(model, inputs, schemaByKey, lastClosed, view, t, valueOf);
        const value = applyDriver('orders_x_ticket', ctx, view, lineKey, t);
        setCell(model, view, lineKey, t, value);
        return;
    }

    if (entry.kind === 'subtotal') {
        const group = ignoreGroupForParent(schema, lineKey);
        if (group && isGroupIgnored(inputs, view, group)) {
            const driver = activeDriver(inputs, view, lineKey);
            if (!driver) {
                setCell(model, view, lineKey, t, null);
                return;
            }
            const ctx = makeCtx(model, inputs, schemaByKey, lastClosed, view, t, valueOf);
            const value = applyDriver(driver as never, ctx, view, lineKey, t);
            setCell(model, view, lineKey, t, value);
            return;
        }
        return;
    }

    if (entry.kind !== 'input') {
        return;
    }
    if (view !== 'consolidated' && inputs.consolidated_only.includes(lineKey)) {
        setCell(model, view, lineKey, t, null);
        return;
    }

    if (
        inputs.cash_policy?.enabled &&
        view === 'consolidated' &&
        policyOwnedKeys.has(lineKey)
    ) {
        const prev = getNumeric(model, view, lineKey, t - 1);
        setCell(model, view, lineKey, t, prev);
        applyOneTimeItems(model, inputs, schemaByKey, view, lineKey, t, lastClosed);
        return;
    }

    const driver = activeDriver(inputs, view, lineKey);
    if (!driver) {
        if (entry.tab === 'BS') {
            const prev = getNumeric(model, view, lineKey, t - 1);
            setCell(model, view, lineKey, t, prev);
        } else {
            setCell(model, view, lineKey, t, null);
        }
        applyOneTimeItems(model, inputs, schemaByKey, view, lineKey, t, lastClosed);
        return;
    }

    if (driver === 'ignore') {
        setCell(model, view, lineKey, t, null);
        return;
    }

    const ctx = makeCtx(model, inputs, schemaByKey, lastClosed, view, t, valueOf);
    const value = applyDriver(driver as never, ctx, view, lineKey, t);
    setCell(model, view, lineKey, t, value);
    applyOneTimeItems(model, inputs, schemaByKey, view, lineKey, t, lastClosed);
}

export function topoSortPctLines(schema: SchemaEntry[], inputs: CaseInputs): string[] {
    const isInputs = schema.filter(
        (entry) =>
            entry.tab === 'IS' &&
            entry.kind === 'input' &&
            entry.row != null &&
            !OPERATIONAL_KEYS.includes(entry.key) &&
            entry.key !== GPS_KEY &&
            entry.key !== DAYS_OPEN_KEY,
    );
    const keys = isInputs.map((entry) => entry.key);
    const edges = new Map<string, Set<string>>();

    for (const key of keys) {
        edges.set(key, new Set());
        const entry = schema.find((row) => row.key === key);
        if (entry?.formula) {
            for (const dep of formulaDependencies(entry.formula)) {
                edges.get(key)?.add(dep);
            }
        }
        for (const pctView of [...LOCATION_VIEWS, 'consolidated'] as View[]) {
            const state = inputs.drivers[pctView]?.[key];
            if (state?.active === 'pct_of_line' || state?.active === 'dollar_per_line_unit') {
                const ref = state.by_type[state.active]?.params?.ref;
                if (ref) {
                    edges.get(key)?.add(ref);
                    const refEntry = schema.find((row) => row.key === ref);
                    if (refEntry?.formula) {
                        for (const formulaDep of formulaDependencies(refEntry.formula)) {
                            edges.get(key)?.add(formulaDep);
                        }
                    }
                }
            }
        }
    }

    const sorted: string[] = [];
    const visited = new Set<string>();
    const visiting = new Set<string>();

    const visit = (key: string): void => {
        if (visited.has(key)) {
            return;
        }
        if (visiting.has(key)) {
            return;
        }
        visiting.add(key);
        for (const dep of edges.get(key) ?? []) {
            if (keys.includes(dep)) {
                visit(dep);
            } else {
                const refEntry = schema.find((row) => row.key === dep);
                if (refEntry?.formula) {
                    for (const formulaDep of formulaDependencies(refEntry.formula)) {
                        if (keys.includes(formulaDep)) {
                            visit(formulaDep);
                        }
                    }
                }
            }
        }
        visiting.delete(key);
        visited.add(key);
        sorted.push(key);
    };

    for (const key of keys) {
        visit(key);
    }
    return sorted;
}

function materializeIsFormulas(
    model: Model,
    view: View,
    t: number,
    isFormulaEntries: SchemaEntry[],
    valueOf: (lineKey: string) => number | null,
    inputs?: CaseInputs,
    schema?: SchemaEntry[],
): void {
    for (const entry of isFormulaEntries) {
        if (view === 'consolidated' && entry.key === GPS_KEY) {
            continue;
        }
        if (view === 'consolidated' && entry.key === AVG_TICKET_KEY) {
            continue;
        }
        if (view === 'consolidated' && inputs && schema) {
            const group = ignoreGroupForParent(schema, entry.key);
            if (group && isGroupIgnoredAtAnyLocation(inputs, group)) {
                continue;
            }
        }
        const value = valueOf(entry.key);
        setCell(model, view, entry.key, t, value);
    }
}

function recomputeBsFormulas(
    model: Model,
    t: number,
    bsFormulaEntries: SchemaEntry[],
    bsInputEntries: SchemaEntry[],
    skipKeys: Set<string>,
    linkedSeeds: Record<string, number | null>,
    cashOverride?: number | null,
): Record<string, number | null> {
    const valuesByKey: Record<string, number | null> = {};
    for (const entry of bsInputEntries) {
        if (entry.key === CASH_KEY && cashOverride !== undefined) {
            valuesByKey[entry.key] = cashOverride;
        } else {
            valuesByKey[entry.key] = getNumeric(model, 'consolidated', entry.key, t);
        }
    }
    for (const [key, value] of Object.entries(linkedSeeds)) {
        valuesByKey[key] = value;
    }
    for (const entry of bsFormulaEntries) {
        if (!entry.formula) {
            valuesByKey[entry.key] = getNumeric(model, 'consolidated', entry.key, t);
        }
    }

    const activeFormulas = bsFormulaEntries.filter((entry) => !skipKeys.has(entry.key));
    for (const entry of activeFormulas) {
        valuesByKey[entry.key] = evalFormula(entry.formula!, valuesByKey);
    }

    for (const entry of [...bsInputEntries, ...bsFormulaEntries]) {
        if (valuesByKey[entry.key] !== undefined) {
            setCell(model, 'consolidated', entry.key, t, valuesByKey[entry.key] ?? null);
        }
    }
    return valuesByKey;
}

function evaluateBsMonth(
    model: Model,
    inputs: CaseInputs,
    schema: SchemaEntry[],
    schemaByKey: Record<string, SchemaEntry>,
    t: number,
    lastClosed: string,
    bsFormulaEntries: SchemaEntry[],
    bsInputEntries: SchemaEntry[],
    history: History,
    context: EvaluateContext,
    policyMonths: CashPolicyMonthResult[],
    allWaterfall: { value: boolean },
    policyOwnedKeys: Set<string>,
): void {
    const valueOf = makeValueOf(model, 'consolidated', t, schemaByKey, inputs, schema);
    const ctx = makeCtx(model, inputs, schemaByKey, lastClosed, 'consolidated', t, valueOf);
    const bsInputs = schema.filter(
        (entry) =>
            entry.tab === 'BS' &&
            entry.kind === 'input' &&
            entry.row != null &&
            entry.key !== CASH_KEY &&
            !entry.drivers.includes('linked') &&
            !entry.drivers.includes('carry_forward'),
    );

    for (const entry of bsInputs) {
        driveLine(model, inputs, schema, schemaByKey, 'consolidated', entry.key, t, lastClosed, valueOf, policyOwnedKeys);
    }

    const linkedSeeds: Record<string, number | null> = {};
    for (const [lineKey, sourceKey] of Object.entries(LINKED_BS)) {
        const value = applyLinked(ctx, 'consolidated', lineKey, t, sourceKey);
        setCell(model, 'consolidated', lineKey, t, value);
        linkedSeeds[lineKey] = value;
    }

    for (const entry of schema) {
        if (entry.tab === 'BS' && entry.drivers.includes('carry_forward')) {
            if (inputs.cash_policy?.enabled && policyOwnedKeys.has(entry.key)) {
                const prev = getNumeric(model, 'consolidated', entry.key, t - 1);
                setCell(model, 'consolidated', entry.key, t, prev);
                continue;
            }
            const prev = getNumeric(model, 'consolidated', entry.key, t - 1);
            setCell(model, 'consolidated', entry.key, t, prev);
        }
    }

    recomputeBsFormulas(
        model,
        t,
        bsFormulaEntries,
        bsInputEntries,
        SKIP_FORMULA_IN_PROJECTION,
        linkedSeeds,
        0,
    );

    const totalLe = getNumeric(model, 'consolidated', TOTAL_LE_KEY, t);
    const totalAssets = getNumeric(model, 'consolidated', TOTAL_ASSETS_KEY, t);
    const cashBefore = getNumeric(model, 'consolidated', CASH_KEY, t) ?? 0;
    const assetsExCash = (totalAssets ?? 0) - cashBefore;
    const plug = applyPlug(totalLe, assetsExCash, ctx, t);
    setCell(model, 'consolidated', CASH_KEY, t, plug);
    recomputeBsFormulas(
        model,
        t,
        bsFormulaEntries,
        bsInputEntries,
        SKIP_FORMULA_IN_PROJECTION,
        linkedSeeds,
        plug,
    );

    const policy = inputs.cash_policy;
    if (policy?.enabled) {
        const historyMmIdx = history.nHistory - 1;
        const historyMmRaw = history.values.consolidated[MM_KEY]?.[historyMmIdx];
        const historyMmAtT0 =
            typeof historyMmRaw === 'number' && Number.isFinite(historyMmRaw) ? historyMmRaw : 0;

        const access = {
            getNumeric: (lineKey: string, monthT: number) =>
                getNumeric(model, 'consolidated', lineKey, monthT),
            setCell: (lineKey: string, monthT: number, value: number | null) =>
                setCell(model, 'consolidated', lineKey, monthT, value),
        };
        const { month, splitSource, issues } = applyCashPolicyMonth(
            policy,
            t,
            lastClosed,
            historyMmAtT0,
            access,
            context,
        );
        policyMonths.push(month);
        for (const issue of issues) {
            model.diagnostics.push({ code: issue.code, message: issue.message, view: 'consolidated', t });
        }
        if (splitSource !== 'waterfall') {
            allWaterfall.value = false;
        }

        for (const [lineKey, sourceKey] of Object.entries(LINKED_BS)) {
            const value = applyLinked(ctx, 'consolidated', lineKey, t, sourceKey);
            setCell(model, 'consolidated', lineKey, t, value);
            linkedSeeds[lineKey] = value;
        }

        const newCash = getNumeric(model, 'consolidated', CASH_KEY, t);
        recomputeBsFormulas(
            model,
            t,
            bsFormulaEntries,
            bsInputEntries,
            SKIP_FORMULA_IN_PROJECTION,
            linkedSeeds,
            newCash,
        );
    }
}

function recomputeMargins(
    model: Model,
    view: View,
    t: number,
    marginEntries: SchemaEntry[],
    schema: SchemaEntry[],
): void {
    const valuesByKey: Record<string, number | null> = {};
    for (const row of schema) {
        if (row.tab === 'IS') {
            valuesByKey[row.key] = getNumeric(model, view, row.key, t);
        }
    }
    for (const entry of marginEntries) {
        setCell(model, view, entry.key, t, evalFormula(entry.formula!, valuesByKey));
    }
}

function interestView(policy: CaseInputs['cash_policy']): View {
    if (!policy) {
        return 'ga';
    }
    return policy.sweep.yield_income_view ?? 'ga';
}

function appendInterestItems(
    workingInputs: CaseInputs,
    policy: CaseInputs['cash_policy'],
    t: number,
    lastClosedMonth: string,
    history: History,
    model: Model,
): void {
    if (!policy?.enabled || !policy.sweep.enabled) {
        return;
    }
    const historyMmIdx = history.nHistory - 1;
    const historyMmRaw = history.values.consolidated[MM_KEY]?.[historyMmIdx];
    const historyMmAtT0 =
        typeof historyMmRaw === 'number' && Number.isFinite(historyMmRaw) ? historyMmRaw : 0;
    const mm0 =
        t === 1
            ? (policy.sweep.opening_mm_balance ?? historyMmAtT0)
            : (getNumeric(model, 'consolidated', MM_KEY, t - 1) ?? 0);
    const snapshot = policy.sweep.yield_snapshot;
    const apr = snapshot ? (snapshot.apr_by_month[t - 1] ?? 0) : 0;
    const interest = interestForMonth(mm0, apr, policy.sweep.enabled);
    if (interest === 0) {
        return;
    }
    const item: OneTimeItem = {
        id: `policy:interest:${t}`,
        month: monthKeyFromT(lastClosedMonth, t),
        view: workingInputs.consolidated_only.includes(INTEREST_KEY)
            ? 'consolidated'
            : interestView(policy),
        line_key: INTEREST_KEY,
        amount: interest,
        mode: 'incremental',
    };
    workingInputs.one_time_items.push(item);
}

export function evaluate(
    schema: SchemaEntry[],
    history: History,
    inputs: CaseInputs,
    lastClosedMonth: string,
    context: EvaluateContext = {
        last_dist_date: null,
        logged_after_close: [],
        waterfall_mode: null,
        last_closed_month: '',
    },
): Model {
    const policy = inputs.cash_policy;
    if (
        policy?.enabled &&
        policy.sweep.enabled &&
        (policy.sweep.mode ?? 'excess') === 'excess' &&
        context.excess_sweep_by_t === undefined
    ) {
        const probeInputs = structuredClone(inputs);
        probeInputs.cash_policy = {
            ...probeInputs.cash_policy!,
            sweep: {
                ...probeInputs.cash_policy!.sweep,
                mode: 'fixed',
                amount_per_period: 0,
                enabled: true,
            },
        };
        const probe = evaluate(schema, history, probeInputs, lastClosedMonth, context);
        const months = [...(probe.cash_policy?.months ?? [])].sort((a, b) => a.t - b.t);
        const operatingCash = months.map((month) => month.operating_cash);
        const floor = probe.cash_policy?.effective_floor ?? effectiveFloor(policy);
        const sweeps = safeExcessSweeps(operatingCash, floor);
        const excess_sweep_by_t: Record<number, number> = {};
        months.forEach((month, index) => {
            excess_sweep_by_t[month.t] = sweeps[index] ?? 0;
        });
        return evaluate(schema, history, inputs, lastClosedMonth, {
            ...context,
            excess_sweep_by_t,
        });
    }

    const policyEnabled = inputs.cash_policy?.enabled === true;
    const workingInputs: CaseInputs = policyEnabled
        ? { ...inputs, one_time_items: [...inputs.one_time_items] }
        : inputs;
    const model = emptyModel(history.nHistory);
    const { byKey: schemaByKey } = indexSchema(schema);
    loadHistory(model, history, schema);
    const policyMonths: CashPolicyMonthResult[] = [];
    const allWaterfall = { value: true };

    const isFormulaEntries = schema.filter(
        (entry) =>
            entry.tab === 'IS' &&
            entry.formula &&
            (entry.kind === 'subtotal' || entry.kind === 'metric') &&
            entry.row != null,
    );
    isFormulaEntries.sort((a, b) => (a.row ?? 0) - (b.row ?? 0));
    const bsFormulaEntries = schema.filter(
        (entry) =>
            entry.tab === 'BS' &&
            entry.formula &&
            (entry.kind === 'subtotal' || entry.kind === 'metric') &&
            entry.row != null,
    );
    bsFormulaEntries.sort((a, b) => (a.row ?? 0) - (b.row ?? 0));
    const bsInputEntries = schema.filter(
        (entry) => entry.tab === 'BS' && entry.kind === 'input' && entry.row != null,
    );
    const marginEntries = schema.filter(
        (entry) => entry.tab === 'IS' && entry.row == null && entry.kind === 'metric' && entry.formula,
    );
    const pctLines = topoSortPctLines(schema, inputs);
    const policyOwnedKeys = policyEnabled ? policyOwnedLineKeys(schema) : new Set<string>();

    for (let i = 0; i < history.nHistory; i += 1) {
        const t = i - (history.nHistory - 1);
        for (const view of [...LOCATION_VIEWS, 'consolidated'] as View[]) {
            recomputeMargins(model, view, t, marginEntries, schema);
        }
    }

    for (let t = 1; t <= 36; t += 1) {
        if (policyEnabled) {
            appendInterestItems(
                workingInputs,
                workingInputs.cash_policy,
                t,
                lastClosedMonth,
                history,
                model,
            );
        }

        for (const view of LOCATION_VIEWS) {
            const valueOf = makeValueOf(model, view, t, schemaByKey, workingInputs, schema);
            for (const lineKey of OPERATIONAL_KEYS) {
                if (
                    (lineKey === 'is.boh' || lineKey === 'is.foh') &&
                    isLaborBuildupActive(workingInputs, view)
                ) {
                    const hourlyKey =
                        lineKey === 'is.boh' ? 'is.hourly_fte_boh' : 'is.hourly_fte_foh';
                    const shareKey = lineKey === 'is.boh' ? 'boh' : 'foh';
                    const hourlyFte = getNumeric(model, view, hourlyKey, t);
                    const headcount = getNumeric(model, view, 'is.salaried_headcount', t);
                    const shareRaw = history.laborMeta?.salaried_shares?.[view]?.[shareKey];
                    const share =
                        shareRaw != null && Number.isFinite(Number(shareRaw))
                            ? Number(shareRaw)
                            : 0;
                    const hourlyPart =
                        hourlyFte != null ? hourlyFte : view === 'ga' ? 0 : null;
                    if (hourlyPart == null || headcount == null) {
                        setCell(model, view, lineKey, t, null);
                    } else {
                        setCell(model, view, lineKey, t, hourlyPart + headcount * share);
                    }
                    continue;
                }
                driveLine(model, workingInputs, schema, schemaByKey, view, lineKey, t, lastClosedMonth, valueOf, policyOwnedKeys);
            }
            const daysClosed = getNumeric(model, view, DAYS_CLOSED_KEY, t);
            const daysOpen =
                daysClosed != null ? daysInMonth(lastClosedMonth, t) - daysClosed : null;
            setCell(model, view, DAYS_OPEN_KEY, t, daysOpen);
            const avgOrders = getCell(model, view, AVG_ORDERS_KEY, t);
            let totalOrders: number | null = null;
            if (typeof avgOrders === 'number' && typeof daysOpen === 'number') {
                totalOrders = daysOpen === 0 ? 0 : avgOrders * daysOpen;
            }
            setCell(model, view, TOTAL_ORDERS_KEY, t, totalOrders);
            driveLine(
                model,
                workingInputs,
                schema,
                schemaByKey,
                view,
                GPS_KEY,
                t,
                lastClosedMonth,
                valueOf,
                policyOwnedKeys,
                'orders_x_ticket',
            );
            for (const lineKey of pctLines) {
                driveLine(model, workingInputs, schema, schemaByKey, view, lineKey, t, lastClosedMonth, valueOf, policyOwnedKeys);
            }
            for (const group of listEligibleIgnoreGroups(schema)) {
                if (isGroupIgnored(workingInputs, view, group)) {
                    driveLine(
                        model,
                        workingInputs,
                        schema,
                        schemaByKey,
                        view,
                        group.parent,
                        t,
                        lastClosedMonth,
                        valueOf,
                        policyOwnedKeys,
                    );
                }
            }
            materializeIsFormulas(model, view, t, isFormulaEntries, valueOf);
        }

        consolidateIsFromLocations(
            schema,
            workingInputs,
            (view, key) => getCell(model, view, key, t),
            (key) => getCell(model, 'consolidated', key, t),
            (key, value) => setCell(model, 'consolidated', key, t, value),
        );

        for (const lineKey of workingInputs.consolidated_only) {
            const valueOf = makeValueOf(model, 'consolidated', t, schemaByKey, workingInputs, schema);
            driveLine(
                model,
                workingInputs,
                schema,
                schemaByKey,
                'consolidated',
                lineKey,
                t,
                lastClosedMonth,
                valueOf,
                policyOwnedKeys,
            );
        }

        const consValueOf = makeValueOf(model, 'consolidated', t, schemaByKey, workingInputs, schema);
        materializeIsFormulas(model, 'consolidated', t, isFormulaEntries, consValueOf, workingInputs, schema);

        evaluateBsMonth(
            model,
            workingInputs,
            schema,
            schemaByKey,
            t,
            lastClosedMonth,
            bsFormulaEntries,
            bsInputEntries,
            history,
            context,
            policyMonths,
            allWaterfall,
            policyOwnedKeys,
        );

        for (const view of [...LOCATION_VIEWS, 'consolidated'] as View[]) {
            recomputeMargins(model, view, t, marginEntries, schema);
        }
    }

    const cfSchema = loadCashFlowSchema();
    applyCashFlow(model, cfSchema);
    for (let t = 1; t <= 36; t += 1) {
        const eop = getNumeric(model, 'consolidated', 'cf.total_cash_eop', t);
        const cash = getNumeric(model, 'consolidated', TOTAL_CASH_KEY, t);
        if (eop != null && cash != null && Math.abs(eop - cash) > 0.01) {
            model.diagnostics.push({
                code: 'cf_not_tied',
                view: 'consolidated',
                lineKey: 'cf.total_cash_eop',
                t,
                message: `Cash Flow does not tie to the Balance Sheet in M+${t}: EOP cash ${eop} vs Total Cash and Equivalents ${cash}`,
            });
        }
    }

    model.kpis = {
        net_income: {
            t3: getNumeric(model, 'consolidated', NI_KEY, 3),
            t6: getNumeric(model, 'consolidated', NI_KEY, 6),
            t12: getNumeric(model, 'consolidated', NI_KEY, 12),
        },
        cash: {
            t3: getNumeric(model, 'consolidated', CASH_KEY, 3),
            t6: getNumeric(model, 'consolidated', CASH_KEY, 6),
            t12: getNumeric(model, 'consolidated', CASH_KEY, 12),
        },
        total_cash: {
            t3: getNumeric(model, 'consolidated', TOTAL_CASH_KEY, 3),
            t6: getNumeric(model, 'consolidated', TOTAL_CASH_KEY, 6),
            t12: getNumeric(model, 'consolidated', TOTAL_CASH_KEY, 12),
        },
    };

    if (policy?.enabled) {
        model.cash_policy = buildCashPolicyResult(policy, policyMonths, allWaterfall.value);
    } else {
        model.cash_policy = null;
    }

    return model;
}

export function emptyInputs(): CaseInputs {
    return { consolidated_only: [], drivers: {}, one_time_items: [], cash_policy: null };
}
